import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Form, Input, Switch, Button, Card, Select, InputNumber, Tag, Spin, Typography, Radio } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import {
  nonNegativeIntegerValidator,
  noWhitespaceValidator,
} from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Option } = Select;
const { Text } = Typography;

function SubPlanEdit() {
  const { plan_id } = useParams();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const [languages, setLanguages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const notify = useNotify();
  const getLanguages = async () => {
    const response = await masterService.getVideoLanguages({
      isAll: true,
      status: "Y",
    });
    const data = response?.data?.data || response?.data || [];
    const list = data.map((item) => ({
      value: item.lang_id,
      label: item.lang_name,
    }));
    setLanguages(list);
  };

  const getFeatures = async () => {
    const response = await masterService.getFeatureList({ isAll: 1 });
    const data = response.data.data;
    const list = data.map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
      answer_type: item.answer_type,
      order_number: item.order_number,
    }));

    setFeatures(list);
  };

  const getPlanTypes = async () => {
    const response = await masterService.getPlanTypeList({ isAll: 1 });
    const data = response.data.data;
    const list = data.filter(item => item.is_active===true)
    .map((item) => ({
      label: item.name,
      value: item.id,
    }))
    setPlanTypes(list);
  };

  const getCountries = async () => {
    const response = await masterService.getAllCountries({
      isAll: true,
      status: "Y",
    });
    const pickList = (res) =>
      res?.data?.data?.data || res?.data?.data || res?.data || [];
    const data = pickList(response);
    const list = data.filter(item => item.curr_id !== null).map((item) => ({
      label: item.country_name,
      value: item.country_id,
      fk_currency_id: item.curr_id,
    }));
    setCountries(list);
  };

  const getSubPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await masterService.getSingleSubPlan(plan_id);
      const data = response.data.data;
      setPlanDetails(data);
    } catch (error) {
      console.log("Error fetching sub plan details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageTitle(
      plan_id === "new" ? "Add Subscription Plan" : "Edit Subscription Plan"
    );
    if (plan_id !== "new") {
      getSubPlanDetails();
    }
    getLanguages();
    getFeatures();
    getPlanTypes();
    getCountries();
  }, [plan_id, setPageTitle]);

  // Merge plan feature answers with master features list by feature id
  useEffect(() => {
    if (!form || !features || features.length === 0) return;

    console.log("features", features);

    // Base form values from plan details if available
    const baseValues = planDetails ? { ...planDetails } : {};

    const planFeatures = planDetails?.features || [];
    console.log("planFeatures", planFeatures);

    // Align features array order to the master features list and inject answers
    const alignedFeatures = features.map((f) => {
      const fid = f.id;
      const ans = planFeatures.find((item) => item.fk_feature_id === fid) || {};
      return {
        fk_feature_id: fid,
        answer_text: ans.answer_text,
        answer_number: ans.answer_number,
        answer_boolean: ans.answer_boolean,
      };
    });

    form.setFieldsValue({
      ...baseValues,
      features: alignedFeatures,
    });
  }, [features, planDetails]);

  const onFinish = async (values) => {
    const formValues = values;
    formValues.plan_type = formValues?.plan_type?.map((item) => {
      return {
        ...item,
        plan_type_price:
          item?.plan_type_price?.map((price) => {
            const currencyId = countries.find(
              (country) => country.value === price.fk_country_id
            )?.fk_currency_id;

            return {
              ...price,
              fk_currency_id: currencyId,
            };
          }) ?? [],
      };
    });
    console.log("Plan Form Values:", formValues);
    try {
      if (plan_id === "new") {
        const response = await masterService.addSubPlan(formValues);
        console.log("Response:", response);
        if (response.data.status) {
          navigate("/plans");
        } else {
          notify.error({
            message: response?.data?.message || "Failed to add plan",
          });
        }
      } else {
        const response = await masterService.updateSubPlan(plan_id, formValues);
        console.log("Response:", response);
        if (response.data.status) {
          navigate("/plans");
        } else {
          notify.error({
            message: response?.data?.message || "Failed to update plan",
          });
        }
      }
    } catch (error) {
      console.log("Error:", error);
      notify.error({
        message: error?.data?.message || "Failed to add or update plan",
      });
    }
  };

  const renderAnswerField = (feature, index) => {
    switch (feature.answer_type?.toUpperCase()) {
      case "TEXT":
        return (
          <Form.Item
            name={["features", index, "answer_text"]}
            className="m-0 p-0"
            rules={[
              { required: true, message: "Please enter text answer" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="Enter text answer" />
          </Form.Item>
        );

      case "NUMBER":
        return (
          <Form.Item
            name={["features", index, "answer_number"]}
            className="m-0 p-0"
            rules={[{ required: true, message: "Please enter number answer" }]}
          >
            <InputNumber placeholder="Enter number answer" className="w-full" />
          </Form.Item>
        );

      case "BOOLEAN":
        return (
          <Form.Item
            name={["features", index, "answer_boolean"]}
            initialValue={false}
            rules={[{ required: true, message: "Please select Yes or No" }]}
            className="m-0 p-0"
          >
            <Radio.Group>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>
        );

      default:
        return null;
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ is_active: true }}
      >
        <div className="flex flex-col gap-4">
          <Card size="small" title="Basic Information">
            {/* Title */}
            <Form.Item
              name="title"
              label="Title"
              rules={[
                { required: true, message: "Please enter title" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <Input placeholder="Enter plan title" />
            </Form.Item>

            {/* Description */}
            <Form.Item name="description" label="Description">
              <Input.TextArea placeholder="Enter plan description" rows={3} />
            </Form.Item>

            <div className="grid grid-cols-[300px_300px_1fr] gap-6">
              {/* Order Number */}

              <Form.Item
                name="order_number"
                label="Order Number"
                rules={[nonNegativeIntegerValidator]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter order number"
                />
                
              </Form.Item>

              {/* Max Screens */}
              <Form.Item
                  name="max_screens"
                  label="Max Screens"
                  rules={[
                    { required: true, message: "Please enter max screens" },
                    nonNegativeIntegerValidator,
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter max screens"
                    min={1}
                  />
                </Form.Item>
              <div className="grid grid-cols-3 gap-4">
                {/* Is Active */}
                <Form.Item
                  name="is_active"
                  label="Is Active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  name="is_content_downloadable"
                  label="Is Content Downloadable"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  name="is_autopay"
                  label="Auto Pay"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </div>
            </div>
          </Card>

          {/* Multilingual Data */}
          <Form.List name="multilingual_data">
            {(fields, { add, remove }) => (
              <>
                {/* <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-semibold">Multilingual Data</h3>
                  </div> */}
                <Card
                  size="small"
                  title="Multilingual Data"
                  extra={
                    <Button
                      type="text"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                    >
                      Add Language
                    </Button>
                  }
                >
                  {fields.length === 0 && (
                    <div className="text-center p-6">
                      <Text type="secondary">
                        No multilingual data added yet. Click "Add Language" to
                        get started.
                      </Text>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        className="border rounded-md"
                        title={`Language #${name + 1}`}
                        extra={
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            style={{ color: "red" }}
                          />
                        }
                      >
                        <div className="grid grid-cols-[200px_1fr] gap-4">
                          <Form.Item
                            {...restField}
                            name={[name, "fk_lang_id"]}
                            label="Language ID"
                            rules={[
                              { required: true, message: "Enter language ID" },
                            ]}
                          >
                            <Select
                              placeholder="Select language"
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children ?? "")
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            >
                              {languages.map((language) => (
                                <Option
                                  key={language.value}
                                  value={language.value}
                                >
                                  {language.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "title"]}
                            label="Translated Title"
                            rules={[
                              {
                                required: true,
                                message: "Enter translated title",
                              },
                              { validator: noWhitespaceValidator },
                            ]}
                          >
                            <Input placeholder="Enter title" />
                          </Form.Item>
                        </div>

                        <Form.Item
                          {...restField}
                          name={[name, "description"]}
                          label="Translated Description"
                        >
                          <Input.TextArea placeholder="Enter translated description" />
                        </Form.Item>
                      </Card>
                    ))}
                  </div>
                </Card>
              </>
            )}
          </Form.List>

          {/* Features */}

          <Card size="small" title="Features">
            {/* <h3 className="text-lg font-semibold mb-2">Features</h3> */}

            <div className="flex flex-col gap-3">
              {features.length === 0 && (
                <div className="text-center p-6">
                  <Text type="secondary">
                    No features created yet. First add features in the Features
                    master.
                  </Text>
                </div>
              )}
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  className="grid grid-cols-2 items-center gap-4 border-b pb-2"
                >
                  {/* Feature Name - Read Only */}
                  <div>
                    <div>
                      <Tag color="blue">{feature.order_number}</Tag>{" "}
                      {feature.title}
                    </div>
                    <Form.Item
                      name={["features", index, "fk_feature_id"]}
                      initialValue={feature.id}
                      hidden
                    >
                      <Input type="hidden" />
                    </Form.Item>
                  </div>

                  {/* Dynamic Answer Field */}
                  <div>{renderAnswerField(feature, index)}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Plan Types */}
          <Form.List name="plan_type">
            {(fields, { add, remove }) => (
              <>
                {/* <div className="flex justify-between items-center mt-6 mb-2">
                  <h3 className="text-lg font-semibold">Plan Types</h3>
                </div> */}
                <Card
                  size="small"
                  title="Plan Types"
                  extra={
                    <Button
                      type="text"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                    >
                      Add Plan Type
                    </Button>
                  }
                >
                  {fields.length === 0 && (
                    <div className="text-center p-6">
                      <Text type="secondary">
                        No plan types added yet. Click "Add Plan Type" to get
                        started.
                      </Text>
                    </div>
                  )}

                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      className="mb-3 border rounded-md"
                      title={`Plan Type #${name + 1}`}
                      extra={
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: "red" }}
                        />
                      }
                    >
                      <div className="grid grid-cols-3 gap-4">
                        <Form.Item
                          {...restField}
                          name={[name, "fk_plan_type_id"]}
                          label="Plan Type"
                          rules={[
                            {
                              required: true,
                              message: "Please select plan type",
                            },
                          ]}
                        >
                          <Select placeholder="Select plan type">
                            {planTypes.map((planType) => (
                              <Option
                                key={planType.value}
                                value={planType.value}
                              >
                                {planType.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "duration_days"]}
                          label="Duration (Days)"
                          rules={[
                            {
                              required: true,
                              message: "Enter duration in days",
                            },
                            nonNegativeIntegerValidator,
                          ]}
                        >
                          <InputNumber
                            type="number"
                            placeholder="Enter duration in days"
                            style={{ width: "100%" }}
                            min={1}
                            onChange={() => {
                              // Trigger validation of extra_days when duration changes
                              form.validateFields([
                                ["plan_type", name, "extra_days"],
                              ]);
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "extra_days"]}
                          label="Extra Days"
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const durationDays = getFieldValue([
                                  "plan_type",
                                  name,
                                  "duration_days",
                                ]);
                                if (
                                  value &&
                                  durationDays &&
                                  value > durationDays
                                ) {
                                  return Promise.reject(
                                    new Error(
                                      "Extra days cannot be more than duration days"
                                    )
                                  );
                                }
                                return Promise.resolve();
                              },
                            }),
                            nonNegativeIntegerValidator,
                          ]}
                        >
                          <InputNumber
                            placeholder="Enter extra days"
                            style={{ width: "100%" }}
                            min={0}
                          />
                        </Form.Item>
                      </div>

                      {/* Nested plan_type_price */}
                      <Form.List name={[name, "plan_type_price"]}>
                        {(
                          priceFields,
                          { add: addPrice, remove: removePrice }
                        ) => (
                          <>
                            <div className="mb-2 flex justify-between items-center">
                              <h4 className="text-md font-semibold">
                                Plan Type Prices
                              </h4>
                              <Button
                                type="dashed"
                                onClick={() => addPrice()}
                                icon={<PlusOutlined />}
                                size="small"
                              >
                                Add Price
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              {priceFields.map(
                                ({
                                  key: priceKey,
                                  name: priceName,
                                  ...priceRest
                                }) => (
                                  <Card
                                    key={priceKey}
                                    size="small"
                                    className="border rounded-md"
                                    extra={
                                      <MinusCircleOutlined
                                        onClick={() => removePrice(priceName)}
                                        style={{ color: "red" }}
                                      />
                                    }
                                  >
                                    <div className="grid grid-cols-2 gap-4">
                                      <Form.Item
                                        {...priceRest}
                                        name={[priceName, "fk_country_id"]}
                                        label="Country"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Enter country",
                                          },
                                        ]}
                                      >
                                        <Select
                                          placeholder="Select country"
                                          showSearch
                                          optionFilterProp="children"
                                        >
                                          {countries.map((country) => (
                                            <Option
                                              key={country.value}
                                              value={country.value}
                                            >
                                              {country.label}
                                            </Option>
                                          ))}
                                        </Select>
                                      </Form.Item>

                                      <Form.Item
                                        {...priceRest}
                                        name={[priceName, "price"]}
                                        label="Price"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Enter price",
                                          },
                                          nonNegativeIntegerValidator,
                                        ]}
                                      >
                                        <InputNumber
                                          placeholder="Enter price"
                                          style={{ width: "100%" }}
                                        />
                                      </Form.Item>
                                    </div>
                                  </Card>
                                )
                              )}
                            </div>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  ))}
                </Card>
              </>
            )}
          </Form.List>

          {/* Submit */}
          <Form.Item className="flex justify-end">
            <Button type="primary" htmlType="submit" className="mt-4">
              {plan_id === "new" ? "Add Plan" : "Update Plan"}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </Spin>
  );
}

export default SubPlanEdit;
